import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Badge, Spinner, Table } from "react-bootstrap";

import { API_URL } from "../../services/api";
import axios from "axios";

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const SectionCard = ({ icon, title, children }) => (
  <div
    className="mb-4 rounded"
    style={{ border: "1px solid #e0e0e0", overflow: "hidden" }}
  >
    <div
      className="px-3 py-2 d-flex align-items-center gap-2"
      style={{ background: "#f5f7fa", borderBottom: "1px solid #e0e0e0" }}
    >
      <i className={`fas ${icon} text-primary`}></i>
      <span className="fw-semibold" style={{ fontSize: "0.9rem" }}>
        {title}
      </span>
    </div>
    <div className="p-3">{children}</div>
  </div>
);

const Field = ({ label, value }) => (
  <div className="mb-2">
    <span className="text-muted small d-block" style={{ fontSize: "0.75rem" }}>
      {label}
    </span>
    <span style={{ fontSize: "0.875rem" }}>{value || "-"}</span>
  </div>
);

const DetailRegistrasi = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    // Guard against React Strict Mode double-invocation in development
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const fetchDetail = async () => {
      // Resolve id_reg from localStorage token and immediately delete it
      const id_reg = localStorage.getItem(`_dreg_${token}`);
      localStorage.removeItem(`_dreg_${token}`);

      if (!id_reg) {
        setError(
          "Link tidak valid atau sudah kadaluarsa. Silakan buka kembali dari halaman laporan.",
        );
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const res = await axios.get(
          `${API_URL}/pendamping/info-registrasi/${id_reg}`,
        );
        if (res.data.success) {
          setData(res.data.data);
        } else {
          setError("Data tidak ditemukan");
        }
      } catch (err) {
        setError(err.response?.data?.message || "Gagal memuat data registrasi");
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchDetail();
  }, [token]);

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 300 }}
      >
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Memuat data registrasi...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 300 }}
      >
        <div className="text-center text-muted">
          <i className="fas fa-exclamation-circle fa-3x mb-3 text-danger"></i>
          <p>{error || "Data tidak ditemukan"}</p>
        </div>
      </div>
    );
  }

  const {
    pengajuan,
    kbli,
    aspek_legal,
    pabrik,
    outlet,
    penyelia,
    bahan,
    produk,
    sidang_fatwa,
    sertifikat,
    tracking,
    narasi_pph,
  } = data;

  const getStatusBadge = (status) => {
    if (!status) return <Badge bg="secondary">-</Badge>;
    const s = status.toLowerCase();
    if (s.includes("terbit") || s.includes("selesai"))
      return <Badge bg="success">{status}</Badge>;
    if (s.includes("proses") || s.includes("verif"))
      return <Badge bg="primary">{status}</Badge>;
    if (s.includes("tolak") || s.includes("batal"))
      return <Badge bg="danger">{status}</Badge>;
    if (s.includes("kirim") || s.includes("dokumen"))
      return (
        <Badge bg="warning" text="dark">
          {status}
        </Badge>
      );
    return <Badge bg="info">{status}</Badge>;
  };

  return (
    <div style={{ background: "#f8f9fa" }}>
      {/* Header */}
      <div
        style={{
          background:
            "linear-gradient(135deg, var(--primary-color) 0%, var(--primary-hover) 50%, var(--primary-dark) 100%)",
          color: "#fff",
          padding: "16px 24px",
        }}
      >
        <div className="d-flex align-items-center gap-3">
          <button
            className="btn btn-sm"
            style={{
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.5)",
              padding: "2px 10px",
            }}
            onClick={() => navigate(-1)}
          >
            <i className="fas fa-arrow-left me-1"></i>Kembali
          </button>
          <i className="fas fa-file-alt fa-lg"></i>
          <div>
            <h5 className="mb-0 fw-bold">
              {pengajuan?.no_daftar || "Detail Registrasi"}
            </h5>
            <small style={{ opacity: 0.8 }}>
              {pengajuan?.merek_dagang} &mdash; {pengajuan?.nama_pu}
            </small>
          </div>
          <div className="ms-auto">{getStatusBadge(pengajuan?.status_reg)}</div>
        </div>
      </div>

      <div className="p-3">
        {/* Pengajuan */}
        <SectionCard icon="fa-store" title="Informasi Pengajuan">
          <div className="row g-2">
            <div className="col-6 col-md-3">
              <Field label="No. Daftar" value={pengajuan?.no_daftar} />
            </div>
            <div className="col-6 col-md-3">
              <Field
                label="Tgl. Daftar"
                value={formatDate(pengajuan?.tgl_daftar)}
              />
            </div>
            <div className="col-6 col-md-3">
              <Field label="Jenis Daftar" value={pengajuan?.jenis_daftar} />
            </div>
            <div className="col-6 col-md-3">
              <Field label="No. Mohon" value={pengajuan?.no_mohon} />
            </div>
            <div className="col-6 col-md-3">
              <Field
                label="Tgl. Mohon"
                value={formatDate(pengajuan?.tgl_mohon)}
              />
            </div>
            <div className="col-6 col-md-3">
              <Field label="Fasilitasi" value={pengajuan?.fac_name} />
            </div>
            <div className="col-6 col-md-3">
              <Field label="Lembaga" value={pengajuan?.nama_lembaga} />
            </div>
            <div className="col-6 col-md-3">
              <Field label="Status" value={pengajuan?.status_reg} />
            </div>
          </div>
        </SectionCard>

        {/* Pelaku Usaha */}
        <SectionCard icon="fa-building" title="Data Pelaku Usaha">
          <div className="row g-2">
            <div className="col-6 col-md-4">
              <Field label="Nama PU" value={pengajuan?.nama_pu} />
            </div>
            <div className="col-6 col-md-4">
              <Field label="Merek Dagang" value={pengajuan?.merek_dagang} />
            </div>
            <div className="col-6 col-md-4">
              <Field label="Nama PJ" value={pengajuan?.nama_pj} />
            </div>
            <div className="col-6 col-md-4">
              <Field label="Email" value={pengajuan?.email} />
            </div>
            <div className="col-6 col-md-4">
              <Field label="No. Kontak PJ" value={pengajuan?.no_kontak_pj} />
            </div>
            <div className="col-6 col-md-4">
              <Field label="No. Telp" value={pengajuan?.no_telp} />
            </div>
            <div className="col-12 col-md-8">
              <Field label="Alamat" value={pengajuan?.alamat_pu} />
            </div>
            <div className="col-6 col-md-2">
              <Field label="Kota/Kab" value={pengajuan?.kota_pu} />
            </div>
            <div className="col-6 col-md-2">
              <Field label="Provinsi" value={pengajuan?.prov_pu} />
            </div>
            <div className="col-6 col-md-3">
              <Field label="Jenis Layanan" value={pengajuan?.jenis_layanan} />
            </div>
            <div className="col-6 col-md-3">
              <Field label="Jenis Produk" value={pengajuan?.jenis_produk} />
            </div>
            <div className="col-6 col-md-3">
              <Field label="Skala Usaha" value={pengajuan?.skala_usaha} />
            </div>
            <div className="col-6 col-md-3">
              <Field label="Area Pemasaran" value={pengajuan?.area_pemasaran} />
            </div>
          </div>
        </SectionCard>

        {/* KBLI */}
        {kbli?.length > 0 && (
          <SectionCard icon="fa-tag" title="KBLI">
            <div className="table-responsive">
              <Table
                bordered
                size="sm"
                className="mb-0"
                style={{ fontSize: "0.825rem" }}
              >
                <thead className="table-light">
                  <tr>
                    <th>Kode KBLI</th>
                    <th>Nama KBLI</th>
                  </tr>
                </thead>
                <tbody>
                  {kbli.map((k, i) => (
                    <tr key={i}>
                      <td>{k.kbli}</td>
                      <td>{k.nama_kbli}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </SectionCard>
        )}

        {/* Aspek Legal */}
        {aspek_legal?.length > 0 && (
          <SectionCard icon="fa-file-contract" title="Aspek Legal">
            <div className="table-responsive">
              <Table
                bordered
                size="sm"
                className="mb-0"
                style={{ fontSize: "0.825rem" }}
              >
                <thead className="table-light">
                  <tr>
                    <th>Jenis Surat</th>
                    <th>No. Surat</th>
                    <th>Tgl. Surat</th>
                    <th>Instansi Penerbit</th>
                    <th>Masa Berlaku</th>
                  </tr>
                </thead>
                <tbody>
                  {aspek_legal.map((a, i) => (
                    <tr key={i}>
                      <td>{a.jenis_surat}</td>
                      <td>{a.no_surat || "-"}</td>
                      <td>{formatDate(a.tgl_surat)}</td>
                      <td>{a.instansi_penerbit || "-"}</td>
                      <td>{formatDate(a.masa_berlaku)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </SectionCard>
        )}

        {/* Fasilitas Produksi / Pabrik */}
        {pabrik?.length > 0 && (
          <SectionCard icon="fa-industry" title="Fasilitas Produksi">
            <div className="table-responsive">
              <Table
                bordered
                size="sm"
                className="mb-0"
                style={{ fontSize: "0.825rem" }}
              >
                <thead className="table-light">
                  <tr>
                    <th>Nama</th>
                    <th>Alamat</th>
                    <th>Kode Pos</th>
                    <th>Status Milik</th>
                  </tr>
                </thead>
                <tbody>
                  {pabrik.map((p, i) => (
                    <tr key={i}>
                      <td>{p.nama}</td>
                      <td>{p.alamat}</td>
                      <td>{p.kode_pos || "-"}</td>
                      <td>{p.nama_status || p.status_milik}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </SectionCard>
        )}

        {/* Outlet */}
        {outlet?.length > 0 && (
          <SectionCard icon="fa-store-alt" title="Outlet">
            <div className="table-responsive">
              <Table
                bordered
                size="sm"
                className="mb-0"
                style={{ fontSize: "0.825rem" }}
              >
                <thead className="table-light">
                  <tr>
                    <th>Nama</th>
                    <th>Alamat</th>
                  </tr>
                </thead>
                <tbody>
                  {outlet.map((o, i) => (
                    <tr key={i}>
                      <td>{o.nama || "-"}</td>
                      <td>{o.alamat || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </SectionCard>
        )}

        {/* Penyelia Halal */}
        {penyelia?.length > 0 && (
          <SectionCard icon="fa-user-shield" title="Penyelia Halal">
            <div className="table-responsive">
              <Table
                bordered
                size="sm"
                className="mb-0"
                style={{ fontSize: "0.825rem" }}
              >
                <thead className="table-light">
                  <tr>
                    <th>Nama</th>
                    <th>No. KTP</th>
                    <th>No. Sertifikat</th>
                    <th>Tgl. Sertifikat</th>
                    <th>No. SK</th>
                    <th>No. Kontak</th>
                  </tr>
                </thead>
                <tbody>
                  {penyelia.map((p, i) => (
                    <tr key={i}>
                      <td>{p.nama}</td>
                      <td>{p.no_ktp || "-"}</td>
                      <td>{p.no_sertifikat || "-"}</td>
                      <td>{formatDate(p.tgl_sertifikat)}</td>
                      <td>{p.no_sk || "-"}</td>
                      <td>{p.no_kontak || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </SectionCard>
        )}

        {/* Produk */}
        {produk?.length > 0 && (
          <SectionCard icon="fa-box" title="Produk">
            <div className="table-responsive">
              <Table
                bordered
                size="sm"
                className="mb-0"
                style={{ fontSize: "0.825rem" }}
              >
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Nama Produk</th>
                    <th>Jumlah Bahan</th>
                  </tr>
                </thead>
                <tbody>
                  {produk.map((p, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>{p.reg_prod_name}</td>
                      <td>{p.jumlah_bahan}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </SectionCard>
        )}

        {/* Bahan */}
        {bahan?.length > 0 && (
          <SectionCard icon="fa-flask" title={`Bahan (${bahan.length})`}>
            <div className="table-responsive">
              <Table
                bordered
                size="sm"
                className="mb-0"
                style={{ fontSize: "0.8rem" }}
              >
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Nama Bahan</th>
                    <th>Kelompok</th>
                    <th>Jenis</th>
                    <th>Merek</th>
                    <th>Produsen</th>
                    <th>No. Sertifikat Halal</th>
                    <th>Tgl. Berlaku</th>
                  </tr>
                </thead>
                <tbody>
                  {bahan.map((b, i) => {
                    const jenisParts = (b.jenis_bahan || "").split("|");
                    const certified = jenisParts[0] === "certified";
                    return (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td>{b.reg_nama_bahan}</td>
                        <td>{b.kelompok || "-"}</td>
                        <td>
                          <span style={{ fontSize: "0.72rem" }}>
                            {jenisParts[1] || "-"}
                          </span>{" "}
                          <Badge
                            bg={certified ? "success" : "secondary"}
                            style={{ fontSize: "0.65rem" }}
                          >
                            {certified ? "Bersertifikat" : "Positive List"}
                          </Badge>
                        </td>
                        <td>{b.merek || "-"}</td>
                        <td>{b.produsen || "-"}</td>
                        <td>{b.no_sertifikat_halal || "-"}</td>
                        <td>{formatDate(b.tgl_berlaku_sertifikat)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </SectionCard>
        )}

        {/* Sertifikat */}
        {sertifikat?.length > 0 && (
          <SectionCard icon="fa-certificate" title="Sertifikat Halal">
            <div className="table-responsive">
              <Table
                bordered
                size="sm"
                className="mb-0"
                style={{ fontSize: "0.825rem" }}
              >
                <thead className="table-light">
                  <tr>
                    <th>No. Sertifikat</th>
                    <th>Tgl. Terbit</th>
                    <th>Tgl. Berlaku</th>
                  </tr>
                </thead>
                <tbody>
                  {sertifikat.map((s, i) => (
                    <tr key={i}>
                      <td>{s.no_sertifikat || "-"}</td>
                      <td>{formatDate(s.tgl_terbit)}</td>
                      <td>{formatDate(s.tgl_berlaku)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </SectionCard>
        )}

        {/* Sidang Fatwa */}
        {sidang_fatwa?.length > 0 && (
          <SectionCard icon="fa-gavel" title="Sidang Fatwa">
            <div className="table-responsive">
              <Table
                bordered
                size="sm"
                className="mb-0"
                style={{ fontSize: "0.825rem" }}
              >
                <thead className="table-light">
                  <tr>
                    <th>Tanggal</th>
                    <th>Hasil</th>
                  </tr>
                </thead>
                <tbody>
                  {sidang_fatwa.map((s, i) => (
                    <tr key={i}>
                      <td>{formatDate(s.tgl_sidang)}</td>
                      <td>{s.hasil || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </SectionCard>
        )}

        {/* Narasi PPH */}
        {narasi_pph?.length > 0 && (
          <SectionCard
            icon="fa-file-alt"
            title={`Narasi PPH (${narasi_pph.length})`}
          >
            {narasi_pph.map((n, i) => (
              <div
                key={n.id || i}
                className="p-3 rounded mb-2"
                style={{
                  background: "#f8f9fa",
                  border: "1px solid #e0e0e0",
                  fontSize: "0.85rem",
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.6,
                }}
              >
                {n.narasi}
              </div>
            ))}
          </SectionCard>
        )}

        {/* Tracking / Riwayat Status */}
        {tracking?.length > 0 && (
          <SectionCard
            icon="fa-history"
            title={`Riwayat Tracking (${tracking.length})`}
          >
            <div style={{ position: "relative", paddingLeft: 28 }}>
              {/* vertical line */}
              <div
                style={{
                  position: "absolute",
                  left: 9,
                  top: 8,
                  bottom: 8,
                  width: 2,
                  background: "#dee2e6",
                }}
              />
              {tracking.map((t, i) => {
                const s = (t.status_desc || "").toLowerCase();
                let dotColor = "#6c757d";
                if (s.includes("terbit") || s.includes("selesai"))
                  dotColor = "#198754";
                else if (s.includes("dikembalikan") || s.includes("batal"))
                  dotColor = "#dc3545";
                else if (s.includes("proses") || s.includes("submitted"))
                  dotColor = "#0d6efd";
                else if (s.includes("kirim")) dotColor = "#ffc107";
                return (
                  <div
                    key={t.log_id || i}
                    className="mb-3"
                    style={{ position: "relative" }}
                  >
                    {/* dot */}
                    <div
                      style={{
                        position: "absolute",
                        left: -20,
                        top: 4,
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        background: dotColor,
                        border: "2px solid #fff",
                        boxShadow: `0 0 0 2px ${dotColor}`,
                        zIndex: 1,
                      }}
                    />
                    <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                      <span
                        className="fw-semibold"
                        style={{ fontSize: "0.85rem", color: dotColor }}
                      >
                        {t.status_desc}
                      </span>
                      <span
                        className="text-muted"
                        style={{ fontSize: "0.75rem" }}
                      >
                        <i className="fas fa-clock me-1"></i>
                        {new Date(t.tanggal).toLocaleString("id-ID", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                      <span
                        className="text-muted"
                        style={{ fontSize: "0.75rem" }}
                      >
                        <i className="fas fa-user me-1"></i>
                        {t.userid}
                      </span>
                    </div>
                    {t.keterangan && (
                      <div
                        className="p-2 rounded"
                        style={{
                          background: "#fff8f0",
                          border: "1px solid #ffe0b2",
                          fontSize: "0.8rem",
                          whiteSpace: "pre-wrap",
                          lineHeight: 1.5,
                        }}
                      >
                        {t.keterangan}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </SectionCard>
        )}
      </div>
    </div>
  );
};

export default DetailRegistrasi;
